<template>
  <Dialog
    :visible="visible"
    modal
    :closable="false"
    :style="{ width: '50rem' }"
    @update:visible="$emit('update:visible', $event)"
  >
    <template #header>
      <div class="flex w-full justify-between items-center">
        <div>
          <h3 class="text-xl font-semibold">
            Publish Changes
          </h3>
          <div v-if="lastPublishedAt" class="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Last published {{ lastPublishedTimeAgo }} ({{ lastPublishedFormatted }})
          </div>
        </div>
        <div class="flex space-x-4">
          <Button
            severity="secondary"
            label="Cancel"
            outlined
            @click="$emit('update:visible', false)"
          />
          <Button
            :label="hasNoSubscription ? 'Start trial & publish' : 'Publish'"
            :loading="isPublishing"
            :disabled="isPublishing"
            @click="handlePublish"
          />
        </div>
      </div>
    </template>

    <div class="space-y-6">
      <div
        v-if="shouldShowGoLiveSummary"
        data-test="publish-go-live-summary"
        class="rounded-xl border border-surface-200 bg-white p-4 shadow-sm dark:border-white/10 dark:bg-white/5"
      >
        <h4 class="text-base font-semibold text-brand-headline dark:text-white">
          Review what will go live
        </h4>
        <p class="mt-2 text-sm text-brand-text dark:text-gray-300">
          You are about to publish the drafted setup to your site. Review the summary first; the full technical diff is available below.
        </p>
        <ul role="list" class="mt-3 space-y-2 text-sm text-brand-text dark:text-gray-300">
          <li
            v-for="bullet in goLiveSummaryBullets"
            :key="bullet"
            class="flex items-start gap-2"
          >
            <span class="mt-1 h-2 w-2 rounded-full bg-brand-link" aria-hidden="true" />
            <span>{{ bullet }}</span>
          </li>
        </ul>
      </div>

      <!-- Feature Stripping Warning -->
      <div v-if="strippedFeatures.length > 0" class="rounded-md bg-amber-400/20 px-4 py-3 ring-1 ring-amber-400/50 dark:bg-amber-900/30 dark:ring-amber-600/50">
        <div class="flex items-start gap-3">
          <ExclamationTriangleIcon class="h-5 w-5 flex-shrink-0 text-amber-600 dark:text-amber-400 mt-0.5" />
          <div class="space-y-2">
            <p class="text-sm font-medium text-amber-800 dark:text-amber-200">
              Some features won't be included in this publish because they require a higher plan:
            </p>
            <ul class="space-y-1">
              <li
                v-for="feature in strippedFeatures"
                :key="feature.key"
                class="flex items-center gap-2 text-sm text-amber-700 dark:text-amber-300"
              >
                <LockClosedIcon class="h-4 w-4 flex-shrink-0" />
                <span>{{ feature.label }} — requires {{ feature.requiredTier }}</span>
              </li>
            </ul>
            <div>
              <router-link
                :to="{ name: ROUTES.UPGRADE }"
                class="inline-flex items-center gap-1 text-sm font-semibold text-amber-800 underline hover:text-amber-900 dark:text-amber-200 dark:hover:text-amber-100"
              >
                Upgrade
                <ArrowRightIcon class="h-3.5 w-3.5" />
              </router-link>
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="hasNoSubscription"
        data-test="publish-go-live-trial-callout"
        class="rounded-xl border border-brand-link/20 bg-brand-link/5 p-4 dark:border-brand-link/30 dark:bg-brand-link/10"
      >
        <h4 class="text-base font-semibold text-brand-headline dark:text-white">
          Going live starts your 14-day trial
        </h4>
        <p class="mt-2 text-sm text-brand-text dark:text-gray-300">
          After this review, you’ll choose a plan and add a card. Your 14-day trial starts when that step succeeds, and RightMessage immediately attempts this publish; your card will not be charged during the trial.
        </p>
      </div>

      <!-- Changes Summary -->
      <div v-if="changesList.length > 0">
        <div v-if="hasNoSubscription" class="space-y-3">
          <button
            type="button"
            data-test="publish-technical-changes-disclosure"
            class="inline-flex items-center rounded-lg border border-surface-300 px-3 py-2 text-sm font-semibold text-brand-text transition hover:border-brand-link hover:text-brand-link dark:border-white/10 dark:text-gray-200 dark:hover:border-brand-link dark:hover:text-white"
            @click="showTechnicalChanges = !showTechnicalChanges"
          >
            {{ showTechnicalChanges ? 'Hide technical changes' : 'Show technical changes' }}
          </button>

          <div v-if="showTechnicalChanges">
            <h4 class="mb-3 text-base font-semibold text-brand-headline dark:text-gray-100">
              What's Changed:
            </h4>
            <div class="rounded-lg border border-gray-200 overflow-hidden dark:border-gray-700">
              <div
                v-for="(categoryChanges, category) in groupedChanges"
                :key="category"
                class="border-b border-gray-200 last:border-b-0 dark:border-gray-700"
              >
                <div class="bg-gray-50 px-4 py-2 dark:bg-gray-800">
                  <div class="text-sm font-medium text-brand-headline dark:text-gray-100">
                    {{ category }}
                    <span class="ml-2 text-gray-500 dark:text-gray-300">
                      ({{ getCategorySummary(categoryChanges) }})
                    </span>
                  </div>
                </div>
                <div class="space-y-3 px-4 py-3">
                  <div
                    v-for="change in categoryChanges"
                    :key="change.id"
                    class="space-y-0"
                  >
                    <div class="flex items-center space-x-2 text-sm">
                      <i
                        :class="{
                          'pi pi-plus-circle text-green-600': change.type === 'added',
                          'pi pi-pencil text-blue-600': change.type === 'modified',
                          'pi pi-minus-circle text-red-600': change.type === 'deleted'
                        }"
                        class="text-xs"
                      />
                      <span class="font-medium text-brand-headline dark:text-gray-100">{{ getCleanName(change) }}</span>
                      <span
                        v-if="change.type !== 'modified'"
                        class="text-xs capitalize text-gray-500 dark:text-gray-300"
                      >
                        ({{ change.type }})
                      </span>
                    </div>
                    <div
                      v-if="isGeneratingMessage || aiDescriptions[change.id] || (change.details && change.details.length > 0)"
                      class="ml-5 text-xs text-brand-text/80 dark:text-gray-300"
                    >
                      <Skeleton
                        v-if="isGeneratingMessage && !aiDescriptions[change.id]"
                        width="20rem"
                        height="0.75rem"
                      />
                      <span v-else>{{ getCleanDescription(change) }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <template v-else>
          <h4 class="mb-3 text-base font-semibold text-brand-headline dark:text-gray-100">
            What's Changed:
          </h4>
          <div class="rounded-lg border border-gray-200 overflow-hidden dark:border-gray-700">
            <div
              v-for="(categoryChanges, category) in groupedChanges"
              :key="category"
              class="border-b border-gray-200 last:border-b-0 dark:border-gray-700"
            >
              <div class="bg-gray-50 px-4 py-2 dark:bg-gray-800">
                <div class="text-sm font-medium text-brand-headline dark:text-gray-100">
                  {{ category }}
                  <span class="ml-2 text-gray-500 dark:text-gray-300">
                    ({{ getCategorySummary(categoryChanges) }})
                  </span>
                </div>
              </div>
              <div class="space-y-3 px-4 py-3">
                <div
                  v-for="change in categoryChanges"
                  :key="change.id"
                  class="space-y-0"
                >
                  <div class="flex items-center space-x-2 text-sm">
                    <i
                      :class="{
                        'pi pi-plus-circle text-green-600': change.type === 'added',
                        'pi pi-pencil text-blue-600': change.type === 'modified',
                        'pi pi-minus-circle text-red-600': change.type === 'deleted'
                      }"
                      class="text-xs"
                    />
                    <span class="font-medium text-brand-headline dark:text-gray-100">{{ getCleanName(change) }}</span>
                    <span
                      v-if="change.type !== 'modified'"
                      class="text-xs capitalize text-gray-500 dark:text-gray-300"
                    >
                      ({{ change.type }})
                    </span>
                  </div>
                  <div
                    v-if="isGeneratingMessage || aiDescriptions[change.id] || (change.details && change.details.length > 0)"
                    class="ml-5 text-xs text-brand-text/80 dark:text-gray-300"
                  >
                    <Skeleton
                      v-if="isGeneratingMessage && !aiDescriptions[change.id]"
                      width="20rem"
                      height="0.75rem"
                    />
                    <span v-else>{{ getCleanDescription(change) }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>

      <!-- Commit Message -->
      <FormGroup label="Publish Message (Optional)">
        <Textarea
          :model-value="publishMessage"
          @update:model-value="handlePublishMessageInput"
          :placeholder="isGeneratingMessage ? 'Generating message...' : 'Describe what changed in this publish'"
          rows="6"
          class="w-full font-mono text-sm"
          :class="{ '!bg-transparent !opacity-60': isGeneratingMessage }"
        />
        <template #description>
          <div class="flex items-center space-x-2 text-sm text-gray-500 mt-2">
            <i class="pi pi-info-circle" />
            <span>
              {{
                isGeneratingMessage
                  ? 'Generating an AI suggestion. You can edit or publish the default message now.'
                  : 'This message will be saved with your publish for future reference.'
              }}
            </span>
          </div>
        </template>
      </FormGroup>

      <!-- Debug Section (Local Only) -->
      <FormGroup v-if="isLocal" label="Debug: Raw Diff (Local Only)">
        <Textarea
          :modelValue="rawDiffDebug"
          readonly
          rows="12"
          class="w-full font-mono text-xs"
        />
      </FormGroup>
    </div>
  </Dialog>
</template>

<script setup lang="ts">
import type { ConfigChange } from '@/composables/useConfigChanges'
import * as jsondiffpatch from 'jsondiffpatch'
import { ExclamationTriangleIcon, LockClosedIcon, ArrowRightIcon } from '@heroicons/vue/24/outline'

const props = defineProps<{
  visible: boolean
  changes: ConfigChange[]
  isPublishing?: boolean
}>()

const emit = defineEmits<{
  (e: 'update:visible', value: boolean): void
  (e: 'publish', message: string): void
}>()

const { enrichChangesWithDescriptions } = useRightyHeadless()
const { stripHtmlFromDiff } = useConfigChanges()
const configStore = useConfigStore()
const subscriptionStore = useSubscriptionStore()
const { hasNoSubscription } = storeToRefs(subscriptionStore)

// Import stripHtml for cleaning HTML from display text
import { stripHtml } from '@/services/string'
const { publish, fullResponse, currentTeam, config } = storeToRefs(configStore)

const { isFeatureLocked, requiredTierForFeature, tierDisplayName } = useFeatureGating()

// Detect features that will be stripped from the published config
const strippedFeatures = computed(() => {
  const stripped: { key: string; label: string; requiredTier: string }[] = []

  // Check campaigns: user has campaigns configured but lacks the feature
  const campaigns = config.value?.campaigns || []
  if (campaigns.length > 0 && isFeatureLocked('campaigns')) {
    stripped.push({
      key: 'campaigns',
      label: 'Website personalization campaigns',
      requiredTier: tierDisplayName(requiredTierForFeature('campaigns')),
    })
  }

  // Check goals: user has goals configured but lacks the feature
  const goals = config.value?.goals || []
  if (goals.length > 0 && isFeatureLocked('goals')) {
    stripped.push({
      key: 'goals',
      label: 'Conversion goals',
      requiredTier: tierDisplayName(requiredTierForFeature('goals')),
    })
  }

  return stripped
})

const isLocal = import.meta.env.VITE_APP_ENV === 'development'

const PUBLISH_MESSAGE_GENERATION_TIMEOUT_MS = 10_000

const publishMessage = ref('')
const isGeneratingMessage = ref(false)
const aiDescriptions = ref<Record<string, string>>({})
const publishMessageWasEdited = ref(false)
const generationToken = ref(0)
const showTechnicalChanges = ref(false)
// Last published timestamp
const lastPublishedAt = computed(() => currentTeam.value?.last_published_at)
const lastPublishedTimeAgo = computed(() => {
  if (!lastPublishedAt.value) return ''
  return useTimeAgo(lastPublishedAt.value).value
})
const lastPublishedFormatted = computed(() => {
  if (!lastPublishedAt.value) return ''
  return useDateFormat(lastPublishedAt.value, 'MMM D, YYYY h:mm A').value
})

// Debug: Show raw diff for local development
const rawDiffDebug = computed(() => {
  if (!fullResponse.value) return 'No config loaded'

  const draft = fullResponse.value.draft
  const stable = fullResponse.value.stable

  if (!draft || !stable) return 'Missing draft or stable config'

  const diff = jsondiffpatch.diff(toRaw(stable), toRaw(draft))

  return JSON.stringify({
    hasDiff: !!diff,
    changes: props.changes,
    diff: diff
  }, null, 2)
})

const changesList = computed(() => props.changes || [])

const groupedChanges = computed(() => {
  return changesList.value.reduce((acc, change) => {
    if (!acc[change.category]) {
      acc[change.category] = []
    }
    acc[change.category].push(change)
    return acc
  }, {} as Record<string, ConfigChange[]>)
})

const shouldShowGoLiveSummary = computed(() => hasNoSubscription.value && changesList.value.length > 0)

const goLiveSummaryBullets = computed(() => {
  const categoriesAndNames = changesList.value.map((change) =>
    `${change.category} ${change.name}`.toLowerCase()
  )

  const hasFlowOrWidgetDraft = changesList.value.some(
    (change) =>
      change.type === 'added' &&
      /(flow|funnel|widget|cta)/i.test(`${change.category} ${change.name}`)
  )
  if (hasFlowOrWidgetDraft) {
    return ['CTA funnel and widget draft']
  }

  const hasSiteConfigurationChanges = categoriesAndNames.some((text) =>
    /(settings|config|configuration|site)/i.test(text)
  )
  if (hasSiteConfigurationChanges) {
    return ['Site configuration changes']
  }

  const hasWidgetOrTargetingSettings = categoriesAndNames.some((text) =>
    /(widget|cta|target|targeting|audience|condition|segment|settings)/i.test(text)
  )
  if (hasWidgetOrTargetingSettings) {
    return ['Widget or targeting settings']
  }

  return ['Widget or targeting settings']
})

const getCategorySummary = (changes: ConfigChange[]): string => {
  const counts = changes.reduce((acc, change) => {
    acc[change.type] = (acc[change.type] || 0) + 1
    return acc
  }, {} as Record<string, number>)

  const parts: string[] = []
  if (counts.added) parts.push(`${counts.added} added`)
  if (counts.modified) parts.push(`${counts.modified} modified`)
  if (counts.deleted) parts.push(`${counts.deleted} deleted`)

  return parts.join(', ')
}

const buildFallbackPublishMessage = (changes: ConfigChange[]) => {
  if (changes.length === 0) return ''

  const uniqueCategories = new Set(
    changes
      .map((change) => change.category.trim())
      .filter(Boolean)
  )
  const count = uniqueCategories.size || changes.length

  return `Update ${count} configuration ${count === 1 ? 'section' : 'sections'}`
}

// Helper to get clean display name without HTML tags
const getCleanName = (change: ConfigChange): string => {
  return stripHtml(change.name)
}

// Helper to get clean description without HTML tags
const getCleanDescription = (change: ConfigChange): string => {
  // If we have an AI description, use it (already clean)
  if (aiDescriptions.value[change.id]) {
    return aiDescriptions.value[change.id]
  }

  // Otherwise, strip HTML from details and join them
  if (change.details && change.details.length > 0) {
    return change.details.map(detail => stripHtml(detail)).join(', ')
  }

  return ''
}

const handlePublishMessageInput = (value: string) => {
  publishMessageWasEdited.value = true
  publishMessage.value = value
}

const withPublishMessageTimeout = async <T>(promise: Promise<T>): Promise<T> => {
  let timeoutId: number | null = null

  try {
    return await Promise.race([
      promise,
      new Promise<never>((_, reject) => {
        timeoutId = window.setTimeout(
          () => reject(new Error('Publish message generation timed out')),
          PUBLISH_MESSAGE_GENERATION_TIMEOUT_MS
        )
      })
    ])
  } finally {
    if (timeoutId !== null) window.clearTimeout(timeoutId)
  }
}

const generateAIMessage = async (token: number) => {
  if (changesList.value.length === 0) return

  isGeneratingMessage.value = true

  try {
    const plainChanges = changesList.value.map((change) => ({
      category: change.category,
      type: change.type,
      name: change.name,
      id: change.id,
      details: change.details,
      diff: change.diff ? stripHtmlFromDiff(toRaw(change.diff)) : undefined
    }))

    const result = await withPublishMessageTimeout(enrichChangesWithDescriptions(plainChanges))

    if (generationToken.value !== token) return

    aiDescriptions.value = result.descriptions

    if (!publishMessageWasEdited.value) {
      publishMessage.value = result.summaryMessage
    }
  } catch (error) {
    console.warn('Failed to generate publish message suggestion:', error)
  } finally {
    if (generationToken.value === token) {
      isGeneratingMessage.value = false
    }
  }
}

const handlePublish = () => {
  emit('publish', publishMessage.value)
}

watch(
  () => props.visible,
  (newVal) => {
    if (newVal && changesList.value.length > 0) {
      showTechnicalChanges.value = false
      generationToken.value += 1
      publishMessageWasEdited.value = false
      aiDescriptions.value = {}
      publishMessage.value = buildFallbackPublishMessage(changesList.value)
      nextTick(() => {
        generateAIMessage(generationToken.value)
      })
      return
    }

    if (!newVal) {
      showTechnicalChanges.value = false
      generationToken.value += 1
      isGeneratingMessage.value = false
      publishMessageWasEdited.value = false
      aiDescriptions.value = {}
    }
  }
)
</script>
